import LoadingPresenter from '@components/Loading';
import {
  CapaErrorCode,
  CapaRfqQuotationType,
  useReadRfqQuotationMutation
} from '@generated/graphql';
import useDashboard from '@hooks/useDashboard';
import useQueryString from '@hooks/useQueryString';
import { ReactNode, useEffect, useCallback, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

interface Props {
  refetch: () => void;
  children?: ReactNode;
}

interface EmailLinkQuery {
  quotationId: string;
  partnerId: string;
  rfqId: string;
  chatRoomId: string;
}

const SharedLinkBoundary = ({ children, refetch }: Props) => {
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const { rfqId } = useParams<{ rfqId: string }>();
  const { query } = useQueryString<EmailLinkQuery>();
  const { onUpdateSelectedQuotation } = useDashboard();
  const [read] = useReadRfqQuotationMutation();

  const readMutation = useCallback(async () => {
    if (!rfqId || !query.partnerId) return;
    setLoading(true);

    const { data } = await read({
      variables: {
        input: {
          rfqId,
          partnerId: query.partnerId
        }
      }
    });

    if (data?.readRfqQuotation.result === CapaErrorCode.Success) {
      refetch();
    }

    setLoading(false);
  }, [rfqId, query.partnerId, refetch, read]);

  useEffect(() => {
    const uuidV4Regex = /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i;

    if (
      uuidV4Regex.test(query?.rfqId) &&
      uuidV4Regex.test(query?.quotationId) &&
      uuidV4Regex.test(query?.partnerId) &&
      uuidV4Regex.test(query?.chatRoomId)
    ) {
      readMutation();

      onUpdateSelectedQuotation({
        rfqId: rfqId,
        partnerId: query?.partnerId,
        quotationId: query?.quotationId,
        chatRoomId: query?.chatRoomId,
        quotationType: CapaRfqQuotationType.Detail
      });

      return history.push(`/dashboard/${rfqId}`);
    }
  }, [rfqId, query, history, readMutation, onUpdateSelectedQuotation]);

  return (
    <>
      {loading && <LoadingPresenter open />}
      {children}
    </>
  );
};

export default SharedLinkBoundary;
